import { useMemo } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import {
  GetInboxMessageThreadsQuery,
  MessageThreadStateEnum,
  RecruiterApplicationStateEnum,
  UserApplicationStateEnum,
  useGetInboxMessageThreadsQuery,
  useInboxMessageThreadUpdatedSubscription,
} from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { handleFetchMore } from '@src/util/handleFetchMore';
import { QueryParams } from '../../interfaces/queryParams.interface';
import { FETCH_MORE_LIMIT, FIRST_BATCH_LIMIT } from '../../constants/filters';
import { SelectedCandidatesFilter } from '../../enums/selectedCandidatesFilter.enum';
import { UseMessageThreadsValue, UseMessageThreadsParams } from './interfaces/useMessageThreads.interface';

export const useMessageThreads = ({ currentRecruiterId }: UseMessageThreadsParams): UseMessageThreadsValue => {
  const dispatch = useAppDispatch();

  const { queryParams } = useQueryParams<QueryParams>();

  const filterInputStates = useMemo(() => {
    if (
      queryParams.selectedCandidatesFilter &&
      [SelectedCandidatesFilter.OPEN, SelectedCandidatesFilter.SHORTLISTED].includes(
        queryParams.selectedCandidatesFilter,
      )
    ) {
      return [MessageThreadStateEnum.OPEN];
    }

    if (queryParams.selectedCandidatesFilter === SelectedCandidatesFilter.CLOSED) {
      return [MessageThreadStateEnum.CLOSED];
    }

    return [MessageThreadStateEnum.OPEN, MessageThreadStateEnum.CLOSED];
  }, [queryParams.selectedCandidatesFilter]);

  const recruiterApplicationStates = useMemo(() => {
    if (queryParams.selectedCandidatesFilter === SelectedCandidatesFilter.OPEN) {
      return [RecruiterApplicationStateEnum.ACCEPTED];
    }

    if (queryParams.selectedCandidatesFilter === SelectedCandidatesFilter.SHORTLISTED) {
      return [RecruiterApplicationStateEnum.PENDING];
    }

    return undefined;
  }, [queryParams.selectedCandidatesFilter]);

  const userApplicationStates = useMemo(() => {
    if (queryParams.selectedCandidatesFilter === SelectedCandidatesFilter.OPEN) {
      return [UserApplicationStateEnum.ACCEPTED];
    }

    if (queryParams.selectedCandidatesFilter === SelectedCandidatesFilter.SHORTLISTED) {
      return [UserApplicationStateEnum.ACCEPTED];
    }

    return undefined;
  }, [queryParams.selectedCandidatesFilter]);

  const selectedRecruiters = useMemo<string[]>(() => {
    if (queryParams.selectedOpening) {
      return [];
    }

    if (queryParams.selectedRecruiter) {
      return [queryParams.selectedRecruiter];
    }

    if (currentRecruiterId) {
      return [currentRecruiterId];
    }

    return [];
  }, [queryParams]);

  const {
    data: messageThreadsData,
    loading: isMessageThreadsLoading,
    updateQuery: updateMessageThreadsQuery,
    refetch: refetchMessageThreads,
    fetchMore: fetchMoreMessageThreads,
    networkStatus: messageThreadsNetworkStatus,
  } = useGetInboxMessageThreadsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
    variables: {
      filterInput: {
        openings: queryParams.selectedOpening ? [queryParams.selectedOpening] : [],
        recruiterApplicationStates: recruiterApplicationStates,
        recruiters: selectedRecruiters,
        states: filterInputStates,
        userApplicationStates: userApplicationStates,
      },
      paginationOptions: {
        limit: FIRST_BATCH_LIMIT,
      },
    },
  });

  const messageThreads = messageThreadsData?.messageThreads.result || [];
  const messageThreadsPagination = messageThreadsData?.messageThreads.pagination;
  const isMessageThreadsInitialLoading = [
    NetworkStatus.loading,
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
  ].includes(messageThreadsNetworkStatus);

  const loadMoreMessageThreads = () => {
    const hasPagination = messageThreadsPagination?.hasMoreResults && messageThreadsPagination?.maxCursor;

    if (!isMessageThreadsLoading && hasPagination) {
      handleFetchMore(fetchMoreMessageThreads, {
        updateQuery: (
          prev: GetInboxMessageThreadsQuery,
          { fetchMoreResult }: { fetchMoreResult: GetInboxMessageThreadsQuery },
        ) => {
          if (!fetchMoreResult || !prev?.messageThreads) {
            return prev;
          }

          return {
            messageThreads: {
              ...prev.messageThreads,
              pagination: {
                ...prev.messageThreads.pagination,
                hasMoreResults: fetchMoreResult.messageThreads.pagination.hasMoreResults,
                maxCursor: fetchMoreResult.messageThreads.pagination.maxCursor,
              },
              result: [...prev.messageThreads.result, ...fetchMoreResult.messageThreads.result],
            },
          };
        },
        variables: {
          paginationOptions: {
            limit: FETCH_MORE_LIMIT,
            minCursor: messageThreadsPagination.maxCursor,
          },
        },
      });
    }
  };

  useInboxMessageThreadUpdatedSubscription({
    onSubscriptionData: ({ subscriptionData }) => {
      const isChatOpen = queryParams.threadId === subscriptionData.data?.messageThreadUpdated.updatedMessageThread.id;

      updateMessageThreadsQuery(prev => {
        if (!prev?.messageThreads) {
          return prev;
        }

        const isMessageInList = prev.messageThreads.result.some(messageThread => {
          return messageThread.id === subscriptionData.data?.messageThreadUpdated.updatedMessageThread.id;
        });

        if (!isMessageInList) {
          return {
            ...prev,
            messageThreads: {
              ...prev.messageThreads,
              pagination: {
                ...prev.messageThreads.pagination,
                totalCount: prev.messageThreads.pagination.totalCount + 1,
              },
              result: [
                ...(subscriptionData.data?.messageThreadUpdated.updatedMessageThread
                  ? [
                      {
                        ...subscriptionData.data?.messageThreadUpdated.updatedMessageThread,
                      },
                    ]
                  : []),
                ...prev.messageThreads.result,
              ],
            },
          };
        }

        return {
          ...prev,
          messageThreads: {
            ...prev.messageThreads,
            result: prev.messageThreads.result.map(messageThread => {
              if (messageThread.id === subscriptionData.data?.messageThreadUpdated.updatedMessageThread.id) {
                return {
                  ...messageThread,
                  ...subscriptionData.data.messageThreadUpdated,
                  __typename: messageThread.__typename,
                  isUnread: isChatOpen
                    ? false
                    : subscriptionData.data.messageThreadUpdated.updatedMessageThread.isUnread,
                };
              }

              return messageThread;
            }),
          },
        };
      });
    },
  });

  return {
    isMessageThreadsInitialLoading,
    isMessageThreadsLoading,
    loadMoreMessageThreads,
    messageThreads,
    messageThreadsPagination,
    refetchMessageThreads,
    updateMessageThreadsQuery,
  };
};
